import React, { useState, useEffect } from 'react';
import { AlertCircle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import api from '../../lib/api';

export default function RetentionTab() {
    const [health, setHealth] = useState(null);
    const [churn, setChurn] = useState(null);
    const [cohorts, setCohorts] = useState(null);
    const [correlation, setCorrelation] = useState(null);
    const [nps, setNps] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        Promise.all([
            api.get('/internal/v1/health-scores').catch(() => ({ data: null })),
            api.get('/internal/v1/churn-signals').catch(() => ({ data: null })),
            api.get('/internal/v1/cohorts').catch(() => ({ data: null })),
            api.get('/internal/v1/feature-retention-correlation').catch(() => ({ data: null })),
            api.get('/internal/v1/nps/scores').catch(() => ({ data: null }))
        ]).then(([h, c, co, cr, n]) => { setHealth(h.data); setChurn(c.data); setCohorts(co.data); setCorrelation(cr.data); setNps(n.data); })
        .finally(() => setLoading(false));
    }, []);

    if (loading) return <div className="animate-pulse h-64 bg-slate-900/50 rounded-2xl" />;

    return (
        <div className="space-y-6">
            {nps && (
                <Card className="bg-slate-900/50 border-white/10" data-testid="nps-scores-card">
                    <CardHeader><CardTitle className="text-white">NPS — Satisfaction utilisateurs</CardTitle></CardHeader>
                    <CardContent>
                        {nps.total_responses === 0 ? (
                            <p className="text-sm text-white/50">Aucune réponse pour l'instant. Le sondage s'affiche automatiquement aux admins, agents et vendeurs (1 fois / 90 jours).</p>
                        ) : (
                            <>
                                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4 mb-4">
                                    {[
                                        { value: nps.nps_score, label: 'Score NPS', bg: nps.nps_score >= 30 ? 'bg-green-500/10 border-green-500/20' : nps.nps_score >= 0 ? 'bg-yellow-500/10 border-yellow-500/20' : 'bg-red-500/10 border-red-500/20', color: nps.nps_score >= 30 ? 'text-green-400' : nps.nps_score >= 0 ? 'text-yellow-400' : 'text-red-400' },
                                        { value: nps.promoters, label: 'Promoteurs (9-10)', bg: 'bg-green-500/10 border-green-500/20', color: 'text-green-400' },
                                        { value: nps.passives, label: 'Passifs (7-8)', bg: 'bg-yellow-500/10 border-yellow-500/20', color: 'text-yellow-400' },
                                        { value: nps.detractors, label: 'Détracteurs (0-6)', bg: 'bg-red-500/10 border-red-500/20', color: 'text-red-400' }
                                    ].map((s, i) => (
                                        <div key={i} className={`text-center p-3 rounded-xl border ${s.bg}`}>
                                            <p className={`text-2xl font-bold ${s.color}`}>{s.value}</p>
                                            <p className={`text-xs ${s.color}/60`}>{s.label}</p>
                                        </div>
                                    ))}
                                </div>
                                {nps.recent_responses?.filter(r => r.comment).length > 0 && (
                                    <div className="space-y-2">
                                        {nps.recent_responses.filter(r => r.comment).slice(0, 5).map((r, i) => (
                                            <div key={i} className="p-3 rounded-xl bg-white/[0.03] flex items-start gap-3">
                                                <span className={`text-sm font-bold shrink-0 ${r.score >= 9 ? 'text-green-400' : r.score >= 7 ? 'text-yellow-400' : 'text-red-400'}`}>{r.score}/10</span>
                                                <p className="text-xs text-white/60 flex-1">{r.comment}</p>
                                                <span className="text-[10px] text-white/30 shrink-0">{r.role}</span>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </>
                        )}
                    </CardContent>
                </Card>
            )}

            {health && (
                <Card className="bg-slate-900/50 border-white/10">
                    <CardHeader><CardTitle className="text-white">Santé des agences</CardTitle></CardHeader>
                    <CardContent>
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4 mb-6">
                            {[
                                { value: health.summary.healthy, label: 'En forme', bg: 'bg-green-500/10 border-green-500/20', color: 'text-green-400' },
                                { value: health.summary.at_risk, label: 'À risque', bg: 'bg-orange-500/10 border-orange-500/20', color: 'text-orange-400' },
                                { value: health.summary.dormant, label: 'Inactif', bg: 'bg-red-500/10 border-red-500/20', color: 'text-red-400' },
                                { value: health.summary.avg_health_score, label: 'Score santé moyen', bg: 'bg-white/5', color: 'text-white' }
                            ].map((s, i) => (
                                <div key={i} className={`text-center p-3 rounded-xl border ${s.bg}`}>
                                    <p className={`text-2xl font-bold ${s.color}`}>{s.value}</p>
                                    <p className={`text-xs ${s.color}/60`}>{s.label}</p>
                                </div>
                            ))}
                        </div>
                        <div className="space-y-2">
                            {health.agencies?.map((a, i) => (
                                <div key={i} className={`flex items-center gap-3 p-3 rounded-xl border ${a.status === 'healthy' ? 'bg-green-500/5 border-green-500/10' : a.status === 'at_risk' ? 'bg-orange-500/5 border-orange-500/10' : 'bg-red-500/5 border-red-500/10'}`}>
                                    <div className="w-10 text-center">
                                        <span className={`text-lg font-bold ${a.status === 'healthy' ? 'text-green-400' : a.status === 'at_risk' ? 'text-orange-400' : 'text-red-400'}`}>{a.health_score}</span>
                                    </div>
                                    <span className="text-sm text-white font-medium flex-1">{a.name}</span>
                                    <span className="text-xs text-white/40">{a.mandates} mandats</span>
                                    <span className="text-xs text-white/40">Inactif: {a.days_inactive}j</span>
                                    {a.alerts.length > 0 && <span className="text-[10px] text-orange-400 max-w-xs truncate">{a.alerts[0]}</span>}
                                </div>
                            ))}
                        </div>
                    </CardContent>
                </Card>
            )}

            {churn && churn.agencies?.some(a => a.risk_level !== 'low') && (
                <Card className="bg-slate-900/50 border-red-500/20">
                    <CardHeader><CardTitle className="text-red-400">Signaux de désengagement</CardTitle></CardHeader>
                    <CardContent>
                        <div className="space-y-3">
                            {churn.agencies?.filter(a => a.risk_level !== 'low').map((a, i) => (
                                <div key={i} className={`p-4 rounded-xl border-l-4 ${a.risk_level === 'critical' ? 'border-l-red-500 bg-red-500/5' : a.risk_level === 'high' ? 'border-l-orange-500 bg-orange-500/5' : 'border-l-yellow-500 bg-yellow-500/5'}`}>
                                    <div className="flex items-center justify-between mb-2">
                                        <span className="text-sm font-bold text-white">{a.name}</span>
                                        <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${a.risk_level === 'critical' ? 'bg-red-500/20 text-red-400' : a.risk_level === 'high' ? 'bg-orange-500/20 text-orange-400' : 'bg-yellow-500/20 text-yellow-400'}`}>Risk: {a.risk_score}/100</span>
                                    </div>
                                    <div className="space-y-1">
                                        {a.signals.map((s, j) => <p key={j} className="text-xs text-white/50 flex items-center gap-1"><AlertCircle className="w-3 h-3 text-orange-400 shrink-0" />{s}</p>)}
                                    </div>
                                    <p className="text-xs text-green-400 mt-2 font-medium">Action : {a.action}</p>
                                </div>
                            ))}
                        </div>
                    </CardContent>
                </Card>
            )}

            {cohorts?.cohorts?.length > 0 && (
                <Card className="bg-slate-900/50 border-white/10">
                    <CardHeader><CardTitle className="text-white">Rétention par cohorte</CardTitle></CardHeader>
                    <CardContent>
                        <div className="overflow-x-auto">
                            <table className="w-full text-xs">
                                <thead><tr className="border-b border-white/10">
                                    <th className="text-left p-2 text-white/50">Cohorte</th><th className="text-center p-2 text-white/50">Agences</th>
                                    <th className="text-center p-2 text-white/50">Rét J30</th><th className="text-center p-2 text-white/50">Rét J60</th><th className="text-center p-2 text-white/50">Rét J90</th>
                                </tr></thead>
                                <tbody>
                                    {cohorts.cohorts.map((c, i) => (
                                        <tr key={i} className="border-b border-white/5">
                                            <td className="p-2 text-white font-medium">{c.month}</td>
                                            <td className="p-2 text-center text-white">{c.total}</td>
                                            {[c.retention_j30, c.retention_j60, c.retention_j90].map((val, j) => (
                                                <td key={j} className="p-2 text-center"><span className={`font-bold ${val >= (50 - j * 10) ? 'text-green-400' : val > 0 ? 'text-yellow-400' : 'text-white/30'}`}>{val}%</span></td>
                                            ))}
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </CardContent>
                </Card>
            )}

            {correlation && (
                <Card className="bg-slate-900/50 border-white/10">
                    <CardHeader>
                        <CardTitle className="text-white">Corrélation fonctionnalités - rétention</CardTitle>
                        <p className="text-xs text-white/50 mt-1">{correlation.insight}</p>
                    </CardHeader>
                    <CardContent>
                        <div className="space-y-2">
                            {correlation.correlations?.map((c, i) => (
                                <div key={i} className="flex items-center gap-3 p-3 rounded-xl bg-white/[0.03]">
                                    <span className="text-xs text-white/70 w-36 truncate">{c.label}</span>
                                    <div className="flex-1 h-3 bg-white/5 rounded-full overflow-hidden">
                                        <div className={`h-full rounded-full ${c.retention_lift > 0 ? 'bg-green-500' : 'bg-red-500'}`} style={{ width: `${Math.min(Math.abs(c.retention_lift), 100)}%` }} />
                                    </div>
                                    <span className={`text-xs font-bold w-16 text-right ${c.retention_lift > 0 ? 'text-green-400' : 'text-red-400'}`}>{c.retention_lift > 0 ? '+' : ''}{c.retention_lift}pts</span>
                                    <span className={`text-[10px] px-2 py-0.5 rounded-full ${c.impact === 'high' ? 'bg-green-500/20 text-green-400' : c.impact === 'medium' ? 'bg-yellow-500/20 text-yellow-400' : 'bg-white/5 text-white/40'}`}>{c.impact}</span>
                                </div>
                            ))}
                        </div>
                    </CardContent>
                </Card>
            )}
        </div>
    );
}
